import { css } from '@emotion/react';
import { CardWithID, TributeCardWithID } from '@mythos/game/Rules';
import { CardBack, CardBackView } from 'CardBackView';
import CardView from 'CardView';
import { EmptyCard, EmptyCardView, EmptyTributeView } from 'EmptyCardView';
import invariant from 'invariant';
import React, { Ref, useCallback } from 'react';
import TributeCardView from 'TributeCardView';
import { UIColorPalette } from './ColorPalette';
import HoverCardStore from './HoverCardStore';

type CardEventFunc<TCard> = (card: TCard, e: any) => void;
type BaseCard = { id: string };
type CardRenderer<TCard> = (props: {
  card: TCard;
  ref?: Ref<HTMLDivElement>;
}) => React.ReactElement;
interface Props<TCard extends BaseCard> {
  cards: TCard[];
  renderer: CardRenderer<TCard>;
  hoverRenderer: CardRenderer<TCard>;
  className?: string;
  highlightColorByCardID?: { [k: string]: string };
  onCardClick?: CardEventFunc<TCard>;
  onCardContextMenu?: CardEventFunc<TCard>;
  cardIDToAnimation?: Map<string, HTMLDivElement>;
  cardIndexToAnimation?: Map<number, HTMLDivElement>;
  style?: React.CSSProperties;
}

export type DeckCard = CardBack & {
  id: `deck${string}`;
};

export default function PileView<TCard extends BaseCard>(props: Props<TCard>) {
  let { cards, highlightColorByCardID } = props;
  highlightColorByCardID = highlightColorByCardID || {};

  const onCardEnter = useCallback(
    (card: TCard, _e: any) => {
      const cardToHover = { ...card };

      HoverCardStore.setPosition('bottom-right');
      HoverCardStore.setCard(cardToHover, props.hoverRenderer);
    },
    [props.hoverRenderer],
  );
  const onCardLeave = useCallback((_card: TCard, _e: any) => {
    HoverCardStore.setCard(null, null);
  }, []);

  const renderedCards = cards.map((card, i) => {
    invariant(!!card, 'falsey card in pile %s', card);
    const highlightColor = highlightColorByCardID[card.id];
    return (
      <div css={PileViewStyles.wrapper} key={card.id}>
        {props.renderer({
          card,
          onLeftClick: props.onCardClick,
          onRightClick: props.onCardContextMenu,
          onCardEnter,
          onCardLeave,
          overlayStyle: {
            boxShadow: highlightColor
              ? `0px 0px 12px 4px ${highlightColor}`
              : null,
          },
          overlayCSS: CardOverlayStyle,
          ref: (node: HTMLDivElement) => {
            if (node) {
              props.cardIDToAnimation?.set(card.id, node);
              props.cardIndexToAnimation?.set(i, node);
            } else {
              props.cardIDToAnimation?.delete(card.id);
              props.cardIndexToAnimation?.delete(i);
            }
          },
        } as any)}
      </div>
    );
  });
  return (
    <div
      css={PileViewStyles.container}
      className={props.className}
      style={props.style}
    >
      {renderedCards}
    </div>
  );
}

export type PileCard = EmptyCard | DeckCard | CardWithID;
export const PileCardRenderer: CardRenderer<PileCard> = (props) => {
  const card = props.card;
  if (card.id.startsWith('empty')) {
    // @ts-ignore
    return <EmptyCardView {...props} />;
  } else if (card.id.startsWith('deck')) {
    // @ts-ignore
    return <CardBackView {...props} />;
  }
  // @ts-ignore
  return <CardView {...props} />;
};
export type PileTributeCard = EmptyCard | DeckCard | TributeCardWithID;
export const PileTributeCardRenderer: CardRenderer<PileTributeCard> = (
  props,
) => {
  if (props.card.id.startsWith('empty')) {
    // @ts-ignore
    return <EmptyTributeView {...props} />;
  }
  if (props.card.id.startsWith('deck')) {
    // @ts-ignore
    return <CardBackView {...props} />;
  }
  // @ts-ignore
  return <TributeCardView {...props} />;
};

const CardOverlayStyle = css({
  '@media (hover: hover)': {
    '&:hover': {
      backgroundColor: UIColorPalette.pileCardHoverOverlay,
    },
  },
});

const PileViewStyles = {
  container: css({
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
  }),
  wrapper: css({
    display: 'inline-block',

    padding: 4,

    borderTopWidth: '2px',
    borderTopStyle: 'solid',
    borderTopColor: 'transparent',
    borderBottomWidth: '2px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'transparent',
  }),
};
